import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  coerceLocale,
  rankByMatch,
  catalogBySlug,
  exerciseRegion,
  toggleSetValue,
  type Exercise,
  type ExerciseGroup as ExerciseGroupT,
  type EquipmentKind as EquipmentKindT,
  type MuscleRegion as MuscleRegionT,
} from '@heraion/shared';

import type { ExercisePickerProps } from './pickerProps';
import type { SupersetContext } from './SupersetPanel';
import { compareByRecency } from './recency';
import { supersetAbbrev } from './supersetAbbrev';

// Everything ExercisePicker knows that isn't JSX: catalog load, recency,
// the filter/search funnel, the multi-select and superset-composition
// state, and the four commit handlers. Extracted from ExercisePicker.tsx
// (size budget) so the component is prop-types + markup.
//
// Deliberately NOT a general-purpose hook: it has exactly one caller and
// its return shape is that caller's render needs.

// PE-40 — 'workout' joins the two the picker shipped with. ⛔ It is not a mode
// the picker can enter on its own: the tab only renders where the host supplies
// `workouts` + `onAddWorkout`, so the Logger's picker has two tabs and the
// program editor's has three.
// #85 (#336): 'program' likewise, only with `programs` + `onAddProgramDay`.
export type PickerTab = 'exercises' | 'superset' | 'workout' | 'program';

// Derived from the component's own prop contract rather than restated, so
// the two can't drift. `import type` is erased at compile time, so naming
// ExercisePicker here creates no runtime import cycle.
//
// supersetContext / presentSlugs / presentTreatment are listed separately
// because the caller
// applies their defaults when it destructures props: by the time they get
// here they are always present, which is stricter than Pick would give.
export type ExercisePickerStateOptions = Pick<
  ExercisePickerProps,
  | 'visible'
  | 'catalog'
  | 'loadRecency'
  | 'bookmarks'
  | 'getExerciseName'
  | 'onAddMany'
  | 'onCreateSuperset'
  | 'onClose'
> & {
  supersetContext: SupersetContext | null;
  presentSlugs: ReadonlyArray<string>;
  presentTreatment: NonNullable<ExercisePickerProps['presentTreatment']>;
  eligibleSlugs?: ExercisePickerProps['eligibleSlugs'];
};

export function useExercisePickerState({
  visible,
  supersetContext,
  presentSlugs,
  presentTreatment,
  eligibleSlugs,
  catalog,
  loadRecency,
  bookmarks,
  getExerciseName,
  onAddMany,
  onCreateSuperset,
  onClose,
}: ExercisePickerStateOptions) {
  const locale = coerceLocale(useTranslation().i18n.language);
  const [items, setItems] = useState<Exercise[] | null>(null);
  const [recency, setRecency] = useState<Map<string, string>>(() => new Map());
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [regions, setRegions] = useState<Set<MuscleRegionT>>(() => new Set());
  const [equipment, setEquipment] = useState<Set<EquipmentKindT>>(() => new Set());

  const [tab, setTab] = useState<PickerTab>('exercises');
  // Region/equipment chips are collapsed by default (LG-33): two always-on
  // rows plus their labels cost ~300px, and with the keyboard up the list
  // has ~240px to live in, so they alone pushed every search result off
  // screen. The chip that opens them carries the active count, so a filter
  // narrowing the list can never be hidden.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [pairIdxs, setPairIdxs] = useState<Set<number>>(() => new Set());
  // Optional name typed at creation; empty = use the auto-abbreviation.
  const [nameOverride, setNameOverride] = useState('');

  // On each open: default the tab to Superset when launched from a card's ＋,
  // and reset the transient selection so a previous session doesn't leak.
  useEffect(() => {
    if (!visible) return;
    setTab(supersetContext ? 'superset' : 'exercises');
    setSelected(new Set());
    setPairIdxs(new Set());
    setNameOverride('');
    setQuery('');
  }, [visible, supersetContext]);

  // Refs so a host passing fresh adapter literals per render can't
  // re-trigger the load-on-open effect (the unstable-object-dep lesson).
  const catalogRef = useRef(catalog);
  const recencyRef = useRef(loadRecency);
  useEffect(() => {
    catalogRef.current = catalog;
    recencyRef.current = loadRecency;
  });

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void (async () => {
      const cached = await catalogRef.current.loadCached();
      if (!cancelled && cached !== null) {
        setItems([...cached]);
        setError(null);
      }
      // Recency from the host (mobile: the recent-sessions window the Logger
      // already primes — no extra network call; cold returns an empty map).
      if (recencyRef.current) {
        void recencyRef.current().then((m) => {
          if (!cancelled) setRecency(m);
        });
      }
      try {
        const fresh = await catalogRef.current.fetchAndPersist();
        if (!cancelled) {
          setItems([...fresh]);
          setError(null);
        }
      } catch (err) {
        if (cancelled || cached !== null) return;
        const status = (err as { status?: unknown }).status;
        setError(`api_error_${typeof status === 'number' ? status : 0}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const nameForSlug = useMemo(() => {
    const bySlug = catalogBySlug(items ?? []);
    return (slug: string) => {
      const ex = bySlug.get(slug);
      return ex ? getExerciseName(ex.name) : slug;
    };
  }, [items, getExerciseName]);

  const presentKey = useMemo(() => presentSlugs.join(','), [presentSlugs]);
  const present = useMemo(
    () => new Set(presentKey === '' ? [] : presentKey.split(',')),
    [presentKey],
  );
  const hidePresent = presentTreatment === 'hide';
  // Set once per prop identity: the filter below runs over the whole catalog
  // on every keystroke, and `includes` on an array would make it O(n·m).
  const eligible = useMemo(
    () => (eligibleSlugs ? new Set(eligibleSlugs) : null),
    [eligibleSlugs],
  );

  const filtered = useMemo(() => {
    if (items === null) return null;
    const q = query.trim();
    const cmp = compareByRecency(recency, (slug) => {
      const ex = items.find((x) => x.slug === slug);
      return ex ? ex.name.en : slug;
    });
    const narrowed = items
      // LG-41: an exercise already on the draft is MARKED by the row, not
      // dropped from the list. Only the plan still hides one, and only until
      // its entries carry an id (LG-40) — see `presentTreatment`.
      .filter((ex) => !hidePresent || !present.has(ex.slug))
      // Archived exercises are hidden from the add-picker but stay in the
      // catalog so past sets that reference them still resolve their name
      // (deprecate-without-orphaning, Antenore 2026-06-19: cheese-sandwich &co).
      .filter((ex) => ex.archived !== true)
      .filter((ex) => eligible === null || eligible.has(ex.slug))
      .filter((ex) => {
        if (regions.size === 0) return true;
        const region = exerciseRegion(ex);
        return region ? regions.has(region) : false;
      })
      .filter((ex) => {
        if (equipment.size === 0) return true;
        return ex.equipment ? equipment.has(ex.equipment) : false;
      });
    return rankByMatch(narrowed, q, locale, (a, b) => {
      // Favourites float to the top, then most-recently-logged.
      const fa = bookmarks.isBookmarked(a.slug) ? 0 : 1;
      const fb = bookmarks.isBookmarked(b.slug) ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return cmp(a.slug, b.slug);
    });
    // bookmarks.count (a primitive) changes only on pin/unpin — depending on
    // the whole store object would re-sort every render (it's a fresh object).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, locale, present, hidePresent, eligible, regions, equipment, recency, bookmarks.count]);

  // Shared immutable Set toggle (@heraion/shared) — same helper the
  // Library filters use.
  const toggleSet = toggleSetValue;

  const onRowPress = (ex: Exercise) => setSelected((s) => toggleSet(s, ex.slug));
  const toggleRegion = (region: MuscleRegionT) =>
    setRegions((s) => toggleSet(s, region));
  const toggleEquipment = (kind: EquipmentKindT) =>
    setEquipment((s) => toggleSet(s, kind));
  const togglePair = (idx: number) => setPairIdxs((s) => toggleSet(s, idx));

  const clearFilters = () => {
    setRegions(new Set());
    setEquipment(new Set());
    setQuery('');
  };
  const hasFilters = regions.size + equipment.size > 0 || query.length > 0;
  // Only the chips count here, NOT the query: the search text is visible in
  // its own box, so counting it would report a filter the athlete can
  // already see and make the badge read one too high.
  const activeFilterCount = regions.size + equipment.size;

  // Superset total = the current exercise (if launched from ＋) + picked
  // already-present entries + newly-selected catalog members.
  const currentCount = supersetContext?.currentName ? 1 : 0;
  const supersetTotal = currentCount + pairIdxs.size + selected.size;

  // `days`: the «In which days» answer (#336), passed on only when there is one,
  // so a host that never asked keeps getting the one argument it knows.
  const commitAdd = (days?: number[]) => {
    if (selected.size === 0) return;
    if (days) {
      if (days.length === 0) return;
      onAddMany?.([...selected], days);
    } else onAddMany?.([...selected]);
    onClose();
  };

  const commitSuperset = () => {
    if (supersetTotal < 2) return;
    const memberNames = [
      ...(supersetContext?.currentName ? [supersetContext.currentName] : []),
      ...[...pairIdxs].map(
        (i) => supersetContext?.otherEntries.find((e) => e.idx === i)?.name ?? '',
      ),
      ...[...selected].map(nameForSlug),
    ];
    onCreateSuperset?.({
      addSlugs: [...selected],
      pairEntryIdxs: [...pairIdxs],
      name: nameOverride.trim() || supersetAbbrev(memberNames),
    });
    onClose();
  };

  const insertTemplate = (g: ExerciseGroupT) => {
    // A template is its own complete group: add all its members (those not
    // already present) and name the group after the template.
    //
    // LG-41 deliberately did NOT loosen this one. Marking a row lets the
    // athlete ASK for a second copy; a template insert is one tap that would
    // add several at once, and silently duplicating what is already on the
    // draft is not what "insert this template" means. Pairing with an entry
    // that is already there is what pairEntryIdxs is for.
    const addSlugs = g.exerciseSlugs.filter((slug) => !present.has(slug));
    onCreateSuperset?.({ addSlugs, pairEntryIdxs: [], name: g.name });
    onClose();
  };

  return {
    /** Slugs already on the draft, for the row to mark. Never a filter now. */
    present,
    tab,
    setTab,
    query,
    setQuery,
    regions,
    equipment,
    toggleRegion,
    toggleEquipment,
    selected,
    onRowPress,
    pairIdxs,
    togglePair,
    nameOverride,
    setNameOverride,
    filtered,
    error,
    nameForSlug,
    hasFilters,
    activeFilterCount,
    filtersOpen,
    setFiltersOpen,
    clearFilters,
    supersetTotal,
    commitAdd,
    commitSuperset,
    insertTemplate,
  };
}
